import { Badge } from "../Badge/Badge";
import { RelativeTime } from "../RelativeTime/RelativeTime";
import { RowActions } from "../RowActions/RowActions";
import { cellText, dateCellValue, isDateColumn } from "./cellValue";
import { cellClassName } from "./columnLayout";
import type { Column } from "./DataTableTypes";
import { highlight } from "./highlight";

interface CellProps<R> {
  row: R;
  col: Column<R>;
  /** Current search query, highlighted in the text. */
  query: string;
}

export function DataTableCell<R>({ row, col, query }: CellProps<R>): React.JSX.Element {
  return (
    <td className={cellClassName(col)}>
      <CellContent row={row} col={col} query={query} />
    </td>
  );
}

/** A custom render wins; otherwise the content follows the column type. */
function CellContent<R>({ row, col, query }: CellProps<R>): React.JSX.Element | null {
  if (col.render) return <>{col.render(row, { query })}</>;
  if (col.type === "actions") return col.actions ? <RowActions actions={col.actions(row)} /> : null;
  if (col.type === "badge") return <BadgeCell row={row} col={col} />;
  if (isDateColumn(col)) return <RelativeTime value={dateCellValue(row, col)} />;
  return <TextCell row={row} col={col} query={query} />;
}

function BadgeCell<R>({ row, col }: { row: R; col: Column<R> }): React.JSX.Element {
  const badge = col.badge?.(row);
  return badge ? <Badge label={badge.label} tone={badge.tone} /> : <MissingValue />;
}

/** Text with the search matches highlighted; the full text shows on hover when it may be cut. */
function TextCell<R>({ row, col, query }: CellProps<R>): React.JSX.Element {
  const text = cellText(row, col);
  if (!text) return <MissingValue />;
  return (
    <span className="nb-cell" title={col.wrap ? undefined : text}>
      {highlight(text, query)}
    </span>
  );
}

function MissingValue(): React.JSX.Element {
  return <span className="nb-cell-muted">—</span>;
}
